/**
 * Bryła budynku do projektu dachu 3D: ściany, połacie z okapem, kalenice, narożniki,
 * rynny, rury spustowe, okna, attyki i słupy wiat.
 *
 * Czysta geometria bez three.js: punkty to tablice [x, y, z] w metrach, układ sceny
 * z PLAN_PROJEKT_DACHU_3D.md (x = wschód, y = góra, z = południe). Skrzydło to prostokąt
 * długość × szerokość obrócony o `obrot` jak rotation.y w three.js.
 *
 * Każdy dach liczymy w „ramce kanonicznej” skrzydła: oś p wzdłuż kalenicy (w jednospadowym
 * wzdłuż okapu), oś q w poprzek, główny spadek w stronę +q. Ramka to obrót skrzydła
 * o wielokrotność 90°, więc nie zmienia orientacji wielokątów. Wielokąty są przeciwne
 * do ruchu wskazówek zegara oglądane od strony normalnej (z zewnątrz), obrysy [x, z]
 * oglądane z góry.
 */

const RAD = Math.PI / 180
const EPS = 1e-9

/* ═══ Wektory i wielokąty ═══════════════════════════════════════════════════ */

export const dodaj = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
export const odejmij = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
export const skaluj = (a, k) => [a[0] * k, a[1] * k, a[2] * k]
export const skalarny = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
export const wektorowy = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
]
export const dlugoscWektora = (a) => Math.hypot(a[0], a[1], a[2])
export const normalizuj = (a) => {
  const d = dlugoscWektora(a)
  return d < EPS ? [0, 0, 0] : skaluj(a, 1 / d)
}

// Normalna Newella: kierunek zgodny z obiegiem wielokąta, długość = 2 × pole.
export const normalnaWielokata = (w) => {
  const n = [0, 0, 0]
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    n[0] += (a[1] - b[1]) * (a[2] + b[2])
    n[1] += (a[2] - b[2]) * (a[0] + b[0])
    n[2] += (a[0] - b[0]) * (a[1] + b[1])
  }
  return n
}

// Azymut rzutu normalnej: od północy (−z) zgodnie z ruchem wskazówek zegara.
// Płaszczyzna pozioma nie ma kierunku, umownie 180.
export const azymutNormalnej = (n) => {
  if (Math.hypot(n[0], n[2]) < 1e-9) return 180
  const a = ((Math.atan2(n[0], -n[2]) / RAD) % 360 + 360) % 360
  return a > 360 - 1e-9 ? 0 : a
}

// Wachlarz trójkątów: wystarcza, bo ściany i połacie są wypukłe.
export const trojkatyWielokata = (wierzcholki) => {
  const indeksy = []
  for (let i = 1; i < wierzcholki.length - 1; i++) indeksy.push(0, i, i + 1)
  return indeksy
}

// Współrzędne punktu w układzie połaci, w metrach od `poczatek` wzdłuż u i v.
export const uvPolaci = (polac, punkt) => {
  const d = odejmij(punkt, polac.poczatek)
  return [skalarny(d, polac.u), skalarny(d, polac.v)]
}

/**
 * Zamknięta płyta pokrycia: wierzch = wielokąt połaci, spód przesunięty o grubość
 * wzdłuż −n, boki z normalnymi na zewnątrz. Wierzchołki osobne dla każdej ściany
 * (ostre krawędzie). UV w metrach: na wierzchu i spodzie (u, v) połaci, na bokach
 * (droga wzdłuż obwodu, odległość od spodu). Grupy to [początek, liczba] w `indeksy`.
 * Bez podanej grubości bierze `polac.grubosc` z bryły.
 */
export const plytaDachu = (polac, grubosc = polac.grubosc ?? 0.25) => {
  const w = polac.wierzcholki
  const n = polac.normalna
  const k = w.length
  const spodW = w.map((p) => odejmij(p, skaluj(n, grubosc)))
  const pozycje = []
  const normalne = []
  const uv = []
  const indeksy = []
  const wierzcholek = (p, nn, t) => {
    pozycje.push(p[0], p[1], p[2])
    normalne.push(nn[0], nn[1], nn[2])
    uv.push(t[0], t[1])
    return pozycje.length / 3 - 1
  }
  const tr = trojkatyWielokata(w)

  const s0 = indeksy.length
  const gora = w.map((p) => wierzcholek(p, n, uvPolaci(polac, p)))
  tr.forEach((i) => indeksy.push(gora[i]))
  const wierzch = [s0, indeksy.length - s0]

  const s1 = indeksy.length
  const nDol = skaluj(n, -1)
  const dol = spodW.map((p, i) => wierzcholek(p, nDol, uvPolaci(polac, w[i])))
  for (let i = 0; i < tr.length; i += 3) indeksy.push(dol[tr[i]], dol[tr[i + 2]], dol[tr[i + 1]])
  const spod = [s1, indeksy.length - s1]

  const s2 = indeksy.length
  let droga = 0
  for (let i = 0; i < k; i++) {
    const j = (i + 1) % k
    const e = odejmij(w[j], w[i])
    const dl = dlugoscWektora(e)
    if (dl < EPS) continue
    const nb = normalizuj(wektorowy(e, n))
    const a = wierzcholek(w[i], nb, [droga, grubosc])
    const b = wierzcholek(spodW[i], nb, [droga, 0])
    const c = wierzcholek(spodW[j], nb, [droga + dl, 0])
    const d = wierzcholek(w[j], nb, [droga + dl, grubosc])
    indeksy.push(a, b, c, a, c, d)
    droga += dl
  }
  const boki = [s2, indeksy.length - s2]

  return { pozycje, normalne, uv, indeksy, grupy: { wierzch, spod, boki } }
}

/* ═══ Wartości domyślne ═════════════════════════════════════════════════════ */

export const DACH_DOMYSLNY = {
  typ: 'dwuspadowy', kat: 35, kalenica: 'wzdluz', spadek: 'przod',
  okap: 0.5, okapSzczytowy: 0.3, grubosc: 0.25, attyka: 0.4,
  // Mansardowy: kąt dolnej części i jej wysokość nad ścianą; naczółkowy: wysokość i kąt naczółka.
  katDolny: 65, zalamanie: 2.0, naczolek: 1.2, katNaczolka: 50,
}
const GRUBOSC_ATTYKI = 0.25
const OKNO = { szerokosc: 1.2, wysokosc: 1.4, parapet: 0.9 }
const DRZWI = { szerokosc: 1.0, wysokosc: 2.1 }
const BALKONOWE = { szerokosc: 2.0, wysokosc: 2.2 }
const OKNO_PODDASZA = { szerokosc: 0.9, wysokosc: 1.2, parapet: 0.4 }
const WYSOKOSC_KONDYGNACJI = 2.9
const OD_NAROZA = 0.9
const ZAPAS_POD_DACHEM = 0.2
const PRZED_SCIANA = 0.02
const SLUP = { przekroj: 0.12, wciecie: 0.1, rozstawMax: 3.5 }

const normalizujSkrzydlo = (sk, i) => {
  const rodzaj = sk.rodzaj === 'wiata' ? 'wiata' : 'budynek'
  const dach = { ...DACH_DOMYSLNY, attyka: rodzaj === 'wiata' ? 0 : DACH_DOMYSLNY.attyka, ...(sk.dach || {}) }
  dach.kat = dach.typ === 'plaski' ? 0 : Math.min(80, Math.max(0, Number(dach.kat) || 0))
  return {
    id: sk.id ?? `s${i}`,
    srodek: sk.srodek || [0, 0],
    dlugosc: sk.dlugosc ?? 10,
    szerokosc: sk.szerokosc ?? 8,
    obrot: sk.obrot || 0,
    wysokoscScian: sk.wysokoscScian ?? 3,
    rodzaj,
    dach,
    okna: sk.okna !== false,
    przylega: sk.przylega || null,
  }
}

/* ═══ Ramka kanoniczna i dach ═══════════════════════════════════════════════ */

// Punkt (p, y, q) ramki obróconej o katObrotu → świat; y liczone od terenu.
const ramka = (srodek, katObrotu, y0) => {
  const c = Math.cos(katObrotu * RAD)
  const s = Math.sin(katObrotu * RAD)
  return {
    punkt: (p, y, q) => [srodek[0] + p * c + q * s, y0 + y, srodek[1] - p * s + q * c],
    kierunek: (p, y, q) => [p * c + q * s, y, -p * s + q * c],
  }
}

// Obrót ramki kanonicznej i półwymiary: A wzdłuż p, B wzdłuż q.
const ramkaKanoniczna = (sk) => {
  const { dlugosc: L, szerokosc: S, obrot: th, dach } = sk
  const wzdluz = { obrot: th, A: L / 2, B: S / 2 }
  const wpoprzek = { obrot: th + 90, A: S / 2, B: L / 2 }
  if (dach.typ === 'jednospadowy') {
    if (dach.spadek === 'tyl') return { obrot: th + 180, A: L / 2, B: S / 2 }
    if (dach.spadek === 'prawo') return wpoprzek
    if (dach.spadek === 'lewo') return { obrot: th - 90, A: S / 2, B: L / 2 }
    return wzdluz
  }
  if (dach.typ === 'plaski') return wzdluz
  const r = dach.kalenica === 'wpoprzek' ? wpoprzek : wzdluz
  // Czterospadowy o równych spadkach ma kalenicę wzdłuż dłuższego boku.
  if (dach.typ === 'czterospadowy' && r.A < r.B - EPS) return r === wzdluz ? wpoprzek : wzdluz
  return r
}

/* ═══ Dach z płaszczyzn: asymetryczny, mansardowy, naczółkowy, przedłużony ══════ */

const DACHY_Z_PLASZCZYZN = ['mansardowy', 'naczolkowy']
// Dachy, które przy ścianie innego skrzydła mogą wejść pod jego dach (kosz zamiast szczytu).
const DACHY_PRZEDLUZANE = ['dwuspadowy', 'czterospadowy', 'mansardowy', 'naczolkowy']

// Część wielokąta [[a, b]...] po stronie nx·a + ny·b ≥ c (Sutherland–Hodgman, zachowuje obieg).
const przytnijPolplaszczyzna = (w, nx, ny, c) => {
  const wynik = []
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    const da = nx * a[0] + ny * a[1] - c
    const db = nx * b[0] + ny * b[1] - c
    if (da >= -1e-12) wynik.push(a)
    if ((da >= -1e-12) !== (db >= -1e-12)) {
      const t = da / (da - db)
      wynik.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
  }
  return wynik.length >= 3 ? wynik : []
}

const pole2 = (w) => {
  let s = 0
  for (let i = 0; i < w.length; i++) s += w[i][0] * w[(i + 1) % w.length][1] - w[(i + 1) % w.length][0] * w[i][1]
  return s / 2
}

// Bez powtórzonych i współliniowych wierzchołków (cięcie płaszczyznami zostawia je na stykach).
const uprosc = (w) => {
  const bez = w.filter((x, i) => {
    const n = w[(i + 1) % w.length]
    return Math.hypot(x[0] - n[0], x[1] - n[1]) > 1e-7
  })
  return bez.filter((x, i) => {
    const a = bez[(i + bez.length - 1) % bez.length]
    const b = bez[(i + 1) % bez.length]
    return Math.abs((x[0] - a[0]) * (b[1] - a[1]) - (x[1] - a[1]) * (b[0] - a[0])) > 1e-9
  })
}

/** Część wspólna wielokąta i wielokąta wypukłego (dowolne obiegi; wynik w obiegu `w`). */
const przytnijWypuklym = (w, k) => {
  const kk = pole2(k) < 0 ? [...k].reverse() : k
  let wynik = w
  for (let i = 0; i < kk.length && wynik.length; i++) {
    const a = kk[i]
    const b = kk[(i + 1) % kk.length]
    wynik = przytnijPolplaszczyzna(wynik, a[1] - b[1], b[0] - a[0], (a[1] - b[1]) * a[0] + (b[0] - a[0]) * a[1])
  }
  return wynik.length >= 3 && Math.abs(pole2(wynik)) > 1e-9 ? uprosc(wynik) : []
}

/** Wielokąt wypukły `w` bez wielokąta wypukłego `h`: lista wypukłych kawałków w obiegu `w`. */
const odejmijWypukly = (w, h) => {
  const hh = pole2(h) < 0 ? [...h].reverse() : h
  const kawalki = []
  let reszta = w
  for (let i = 0; i < hh.length && reszta.length; i++) {
    const a = hh[i]
    const b = hh[(i + 1) % hh.length]
    const nx = a[1] - b[1]
    const ny = b[0] - a[0]
    const c = nx * a[0] + ny * a[1]
    const na = przytnijPolplaszczyzna(reszta, -nx, -ny, -c)
    if (na.length && Math.abs(pole2(na)) > 1e-6) kawalki.push(uprosc(na))
    reszta = przytnijPolplaszczyzna(reszta, nx, ny, c)
  }
  return kawalki.filter((x) => x.length >= 3)
}

/**
 * Dach jako płaszczyzny h = c0 + cp·p + cq·q nad prostokątem okapów [p0, p1] × [q0, q1] w ramce kanonicznej
 * (dach to ich minimum). `strona` to kierunek spadku płaszczyzny: '+q' przód, '-q' tył, '+p', '-p' końce.
 * Dwuspadowy z `kat2` ma inny kąt połaci tylnej (kalenica przesuwa się w stronę łagodniejszej połaci),
 * mansardowy ma po dwie płaszczyzny z każdej strony (stroma dolna o kącie `katDolny` do wysokości
 * `zalamanie` nad ścianą, łagodna górna o kącie `kat`), naczółkowy ucina górę szczytów naczółkami o wysokości
 * `naczolek` i kącie `katNaczolka`.
 */
const modelDachu = (d, A, B, H) => {
  const t = Math.tan(d.kat * RAD)
  const o = d.okap
  const g = d.okapSzczytowy
  const pl = (c0, cp, cq, strona) => ({ c: [c0, cp, cq], strona })
  const zasieg = (pa, qa) => ({ p0: -pa, p1: pa, q0: -qa, q1: qa })
  if (d.typ === 'czterospadowy') {
    return { plaszczyzny: [pl(H + t * B, 0, -t, '+q'), pl(H + t * B, 0, t, '-q'), pl(H + t * A, -t, 0, '+p'), pl(H + t * A, t, 0, '-p')], ...zasieg(A + o, B + o) }
  }
  if (d.typ === 'jednospadowy') return { plaszczyzny: [pl(H + t * B, 0, -t, '+q')], ...zasieg(A + g, B + o) }
  if (d.typ === 'mansardowy') {
    const tD = Math.tan(Math.min(80, Math.max(d.kat + 5, d.katDolny ?? 65)) * RAD)
    const hz = Math.min(Math.max(0.3, d.zalamanie ?? 2), Math.max(0.3, (B - 0.5) * tD))
    const gora = H + hz + t * (B - hz / tD)
    return {
      plaszczyzny: [pl(H + tD * B, 0, -tD, '+q'), pl(gora, 0, -t, '+q'), pl(H + tD * B, 0, tD, '-q'), pl(gora, 0, t, '-q')],
      ...zasieg(A + g, B + o),
    }
  }
  if (d.typ === 'naczolkowy') {
    const tn = Math.tan(Math.min(80, Math.max(10, d.katNaczolka ?? 50)) * RAD)
    const yr = H + t * B
    const hn = Math.min(Math.max(0.2, d.naczolek ?? 1.2), Math.max(0.2, yr - H - 0.4), Math.max(0.2, (A - 0.5) * tn))
    const yn = yr - hn
    return {
      plaszczyzny: [pl(H + t * B, 0, -t, '+q'), pl(H + t * B, 0, t, '-q'), pl(yn + tn * A, -tn, 0, '+p'), pl(yn + tn * A, tn, 0, '-p')],
      ...zasieg(A + g, B + o),
    }
  }
  const t2 = Math.tan(Math.min(80, Math.max(5, d.kat2 ?? d.kat)) * RAD)
  return { plaszczyzny: [pl(H + t * B, 0, -t, '+q'), pl(H + t2 * B, 0, t2, '-q')], ...zasieg(A + g, B + o) }
}

/**
 * Połacie z modelu płaszczyzn: każda to prostokąt okapów przycięty do części, w której jej płaszczyzna jest
 * najniższa. Rodzaje krawędzi: na brzegu poziome ze spadkiem na zewnątrz „okap”, poziome od wewnątrz „kalenica”,
 * ukośne „szczytowa”; między płaszczyznami poziome przy przeciwnych spadkach „kalenica”, przy zgodnych
 * „zalamanie” (mansarda), ukośne „naroze”. Obieg z zewnątrz przeciwny do wskazówek zegara, dolna krawędź na początku.
 */
const dachZPlaszczyzn = ({ plaszczyzny, p0, p1, q0, q1 }) => {
  const h = (c, p, q) => c[0] + c[1] * p + c[2] * q
  const prostokat = [[p0, q0], [p1, q0], [p1, q1], [p0, q1]]
  const wynik = {
    plaszczyzny: [], wysokosci: plaszczyzny.map((x) => x.c), kalenice: [], naroza: [], zalamania: [],
    scianyOkapowe: [], zasieg: { p0, p1, q0, q1 },
  }
  const T = 1e-6
  const naBrzegu = (a, b) => {
    if (Math.abs(a[0] - p1) < T && Math.abs(b[0] - p1) < T) return ['+p', [1, 0]]
    if (Math.abs(a[0] - p0) < T && Math.abs(b[0] - p0) < T) return ['-p', [-1, 0]]
    if (Math.abs(a[2] - q1) < T && Math.abs(b[2] - q1) < T) return ['+q', [0, 1]]
    if (Math.abs(a[2] - q0) < T && Math.abs(b[2] - q0) < T) return ['-q', [0, -1]]
    return null
  }
  const okapowe = new Set()
  let yOkapu = Infinity
  plaszczyzny.forEach(({ c, strona }, i) => {
    let w2 = prostokat
    plaszczyzny.forEach(({ c: cj }, j) => {
      if (j !== i && w2.length) w2 = przytnijPolplaszczyzna(w2, cj[1] - c[1], cj[2] - c[2], c[0] - cj[0])
    })
    w2 = w2.length ? uprosc(w2) : w2
    if (w2.length < 3 || Math.abs(pole2(w2)) < 1e-4) return
    let w = w2.map(([p, q]) => [p, h(c, p, q), q])
    if (normalnaWielokata(w)[1] < 0) w = [...w].reverse()
    const spad = [-c[1], -c[2]]
    const rodzaj = (a, b) => {
      const poziom = Math.abs(a[1] - b[1]) < T
      const brzeg = naBrzegu(a, b)
      if (brzeg) {
        if (poziom && spad[0] * brzeg[1][0] + spad[1] * brzeg[1][1] > 1e-9) {
          okapowe.add(brzeg[0])
          yOkapu = Math.min(yOkapu, a[1])
          return 'okap'
        }
        return poziom ? 'kalenica' : 'szczytowa'
      }
      const sp = (a[0] + b[0]) / 2
      const sq = (a[2] + b[2]) / 2
      const j = plaszczyzny.findIndex((x, k) => k !== i && Math.abs(h(x.c, sp, sq) - h(c, sp, sq)) < 1e-6)
      const cj = j >= 0 ? plaszczyzny[j].c : c
      const r = !poziom ? 'naroze' : (spad[0] * -cj[1] + spad[1] * -cj[2] < 0 ? 'kalenica' : 'zalamanie')
      if (j > i) (r === 'naroze' ? wynik.naroza : r === 'kalenica' ? wynik.kalenice : wynik.zalamania).push([a, b])
      return r
    }
    let rodzaje = w.map((a, k) => rodzaj(a, w[(k + 1) % w.length]))
    // Dolna krawędź na początku: okap, a w górnej części mansardy załamanie.
    const k0 = rodzaje.includes('okap') ? rodzaje.indexOf('okap') : rodzaje.indexOf('zalamanie')
    if (k0 > 0) {
      w = [...w.slice(k0), ...w.slice(0, k0)]
      rodzaje = [...rodzaje.slice(k0), ...rodzaje.slice(0, k0)]
    }
    const dl = Math.hypot(spad[0], spad[1]) || 1
    wynik.plaszczyzny.push({ w, u: [spad[1] / dl, 0, -spad[0] / dl], rodzaje, strona })
  })
  wynik.scianyOkapowe = [...okapowe]
  wynik.yOkapu = Number.isFinite(yOkapu) ? yOkapu : null
  return wynik
}

/**
 * Dach w ramce kanonicznej. Zwraca połacie (wierzchołki [p, y, q], kierunek u, rodzaj
 * każdej krawędzi), płaszczyzny dachu jako h = c0 + cp·p + cq·q (do szczytów ścian),
 * kalenice, narożniki, załamania, klucze ścian pod okapem (rynny, rury) i zasięg okapów.
 * Dachy proste liczymy jak dotąd (te same połacie i ich kolejność w zapisanych projektach), asymetryczne,
 * mansardowe, naczółkowe i przedłużone z modelu płaszczyzn. `przedluz = { strona: '+p' | '-p', dlugosc }`:
 * koniec dachu od tej strony sięga dalej (pod dach skrzydła obok), a naczółek albo połać końcowa znika.
 */
const dachKanoniczny = (d, A, B, H, przedluz = null) => {
  const zModelu = przedluz || DACHY_Z_PLASZCZYZN.includes(d.typ)
    || (d.typ === 'dwuspadowy' && d.kat2 != null && Math.abs(d.kat2 - d.kat) > 1e-6)
  if (zModelu && d.typ !== 'plaski') {
    const m = modelDachu(d, A, B, H)
    if (przedluz) {
      m.plaszczyzny = m.plaszczyzny.filter((x) => x.strona !== przedluz.strona)
      if (przedluz.strona === '+p') m.p1 = A + przedluz.dlugosc
      else m.p0 = -(A + przedluz.dlugosc)
    }
    return dachZPlaszczyzn(m)
  }
  const kd = dachProsty(d, A, B, H)
  const e = d.attyka > 0 ? 0 : d.okap
  const { p0, p1, q0, q1 } = d.typ === 'plaski' ? { p0: -A - e, p1: A + e, q0: -B - e, q1: B + e } : modelDachu(d, A, B, H)
  return { zalamania: [], ...kd, zasieg: { p0, p1, q0, q1 } }
}

const dachProsty = (d, A, B, H) => {
  const t = Math.tan(d.kat * RAD)
  const o = d.okap
  const g = d.okapSzczytowy
  if (d.typ === 'plaski') {
    const e = d.attyka > 0 ? 0 : o
    const a = A + e
    const b = B + e
    return {
      plaszczyzny: [{
        w: [[-a, H, b], [a, H, b], [a, H, -b], [-a, H, -b]],
        u: [1, 0, 0],
        rodzaje: ['okap', 'szczytowa', 'szczytowa', 'szczytowa'],
      }],
      wysokosci: [[H, 0, 0]],
      kalenice: [], naroza: [], scianyOkapowe: [],
    }
  }
  const ye = H - o * t
  if (d.typ === 'jednospadowy') {
    const pa = A + g
    const qa = B + o
    const yg = H + (2 * B + o) * t
    return {
      plaszczyzny: [{
        w: [[-pa, ye, qa], [pa, ye, qa], [pa, yg, -qa], [-pa, yg, -qa]],
        u: [1, 0, 0],
        rodzaje: ['okap', 'szczytowa', 'kalenica', 'szczytowa'],
      }],
      wysokosci: [[H + t * B, 0, -t]],
      kalenice: [], naroza: [], scianyOkapowe: ['+q'],
    }
  }
  const yr = H + B * t
  if (d.typ === 'czterospadowy') {
    const Ap = A + o
    const Bp = B + o
    const r = A - B
    const zKalenica = r > 1e-6
    const przod = zKalenica
      ? { w: [[-Ap, ye, Bp], [Ap, ye, Bp], [r, yr, 0], [-r, yr, 0]], rodzaje: ['okap', 'naroze', 'kalenica', 'naroze'] }
      : { w: [[-Ap, ye, Bp], [Ap, ye, Bp], [0, yr, 0]], rodzaje: ['okap', 'naroze', 'naroze'] }
    const tyl = zKalenica
      ? { w: [[Ap, ye, -Bp], [-Ap, ye, -Bp], [-r, yr, 0], [r, yr, 0]], rodzaje: przod.rodzaje }
      : { w: [[Ap, ye, -Bp], [-Ap, ye, -Bp], [0, yr, 0]], rodzaje: przod.rodzaje }
    const rr = zKalenica ? r : 0
    return {
      plaszczyzny: [
        { ...przod, u: [1, 0, 0] },
        { ...tyl, u: [-1, 0, 0] },
        { w: [[Ap, ye, Bp], [Ap, ye, -Bp], [rr, yr, 0]], u: [0, 0, -1], rodzaje: ['okap', 'naroze', 'naroze'] },
        { w: [[-Ap, ye, -Bp], [-Ap, ye, Bp], [-rr, yr, 0]], u: [0, 0, 1], rodzaje: ['okap', 'naroze', 'naroze'] },
      ],
      wysokosci: [[H + t * B, 0, -t], [H + t * B, 0, t], [H + t * A, -t, 0], [H + t * A, t, 0]],
      kalenice: zKalenica ? [[[-r, yr, 0], [r, yr, 0]]] : [],
      naroza: [
        [[Ap, ye, Bp], [rr, yr, 0]], [[Ap, ye, -Bp], [rr, yr, 0]],
        [[-Ap, ye, -Bp], [-rr, yr, 0]], [[-Ap, ye, Bp], [-rr, yr, 0]],
      ],
      scianyOkapowe: ['+q', '-q', '+p', '-p'],
    }
  }
  // dwuspadowy
  const pa = A + g
  const qa = B + o
  const rodzaje = ['okap', 'szczytowa', 'kalenica', 'szczytowa']
  return {
    plaszczyzny: [
      { w: [[-pa, ye, qa], [pa, ye, qa], [pa, yr, 0], [-pa, yr, 0]], u: [1, 0, 0], rodzaje },
      { w: [[pa, ye, -qa], [-pa, ye, -qa], [-pa, yr, 0], [pa, yr, 0]], u: [-1, 0, 0], rodzaje },
    ],
    wysokosci: [[H + t * B, 0, -t], [H + t * B, 0, t]],
    kalenice: [[[-pa, yr, 0], [pa, yr, 0]]],
    naroza: [],
    scianyOkapowe: ['+q', '-q'],
  }
}

// Ściany w ramce kanonicznej: końce [p, q] lewy i prawy (patrząc z zewnątrz), normalna.
const scianyKanoniczne = (A, B) => [
  { klucz: '+q', lewy: [-A, B], prawy: [A, B], n: [0, 0, 1] },
  { klucz: '+p', lewy: [A, B], prawy: [A, -B], n: [1, 0, 0] },
  { klucz: '-q', lewy: [A, -B], prawy: [-A, -B], n: [0, 0, -1] },
  { klucz: '-p', lewy: [-A, -B], prawy: [-A, B], n: [-1, 0, 0] },
]

const wysokoscDachu = (wysokosci, p, q) => Math.min(...wysokosci.map((c) => c[0] + c[1] * p + c[2] * q))

/**
 * Górna krawędź ściany pod dachem od prawego końca (s = 0) do lewego (s = 1):
 * dach to minimum płaszczyzn, więc załamania leżą tam, gdzie dwie płaszczyzny są równe.
 */
const profilSciany = (prawy, lewy, wysokosci) => {
  const naS = (c, s) => c[0] + c[1] * (prawy[0] + (lewy[0] - prawy[0]) * s) + c[2] * (prawy[1] + (lewy[1] - prawy[1]) * s)
  const sy = [0, 1]
  for (let i = 0; i < wysokosci.length; i++) {
    for (let j = i + 1; j < wysokosci.length; j++) {
      const fi0 = naS(wysokosci[i], 0)
      const fj0 = naS(wysokosci[j], 0)
      const mian = (naS(wysokosci[i], 1) - fi0) - (naS(wysokosci[j], 1) - fj0)
      if (Math.abs(mian) < EPS) continue
      const s = (fj0 - fi0) / mian
      if (s > 1e-7 && s < 1 - 1e-7) sy.push(s)
    }
  }
  sy.sort((a, b) => a - b)
  const punkty = []
  sy.forEach((s) => {
    if (punkty.length && s - punkty[punkty.length - 1][0] < 1e-7) return
    punkty.push([s, Math.min(...wysokosci.map((c) => naS(c, s)))])
  })
  // Usuwamy punkty współliniowe, żeby ściana nie miała zbędnych wierzchołków.
  return punkty.filter((p, i) => {
    if (i === 0 || i === punkty.length - 1) return true
    const a = punkty[i - 1]
    const b = punkty[i + 1]
    const h = a[1] + (b[1] - a[1]) * (p[0] - a[0]) / (b[0] - a[0])
    return Math.abs(h - p[1]) > 1e-7
  })
}

// Wysokość górnej krawędzi ściany w odległości x od jej lewego końca.
const wysokoscProfilu = (profil, x, W) => {
  const s = Math.min(1, Math.max(0, 1 - x / W))
  for (let i = 0; i < profil.length - 1; i++) {
    const [s0, h0] = profil[i]
    const [s1, h1] = profil[i + 1]
    if (s <= s1 + 1e-12) return h0 + (h1 - h0) * (s - s0) / (s1 - s0 || 1)
  }
  return profil[profil.length - 1][1]
}

/**
 * Okna jednej ściany: x od lewego końca, yDol od terenu. Okna w równych przęsłach
 * (co najmniej 0,9 m od narożników), na każdej kondygnacji te same osie. Na froncie
 * środkowe okno parteru to drzwi, z tyłu okno balkonowe. Okno zostaje tylko wtedy,
 * gdy mieści się pod dachem z zapasem.
 */
const oknaSciany = ({ W, H, profil, strona, poddasze }) => {
  const wynik = []
  const gora = (x) => wysokoscProfilu(profil, x, W)
  const miesci = (x, szer, yGora) => yGora + ZAPAS_POD_DACHEM <= Math.min(gora(x - szer / 2), gora(x + szer / 2)) + 1e-9
  const n = Math.floor((W + 1e-9) / (2 * OD_NAROZA + OKNO.szerokosc))
  const pietra = Math.max(1, Math.round(H / WYSOKOSC_KONDYGNACJI))
  const hp = H / pietra
  const srodkowy = Math.floor(n / 2)
  for (let f = 0; f < pietra; f++) {
    for (let i = 0; i < n; i++) {
      const x = (i + 0.5) * W / n
      let el = { x, yDol: f * hp + OKNO.parapet, szerokosc: OKNO.szerokosc, wysokosc: OKNO.wysokosc, rodzaj: 'okno' }
      if (f === 0 && i === srodkowy && strona === 'przod') {
        el = { x, yDol: 0, szerokosc: DRZWI.szerokosc, wysokosc: DRZWI.wysokosc, rodzaj: 'drzwi' }
      } else if (f === 0 && i === srodkowy && strona === 'tyl'
        && Math.min(x, W - x) - BALKONOWE.szerokosc / 2 >= OD_NAROZA - 1e-9
        && miesci(x, BALKONOWE.szerokosc, BALKONOWE.wysokosc)) {
        el = { x, yDol: 0, szerokosc: BALKONOWE.szerokosc, wysokosc: BALKONOWE.wysokosc, rodzaj: 'balkonowe' }
      }
      if (miesci(x, el.szerokosc, el.yDol + el.wysokosc)) wynik.push(el)
    }
  }
  // Krótki front bez przęsła na okno dostaje same drzwi.
  if (strona === 'przod' && n === 0 && W >= DRZWI.szerokosc + 2 * 0.5 && miesci(W / 2, DRZWI.szerokosc, DRZWI.wysokosc)) {
    wynik.push({ x: W / 2, yDol: 0, szerokosc: DRZWI.szerokosc, wysokosc: DRZWI.wysokosc, rodzaj: 'drzwi' })
  }
  if (poddasze) {
    const x = W / 2
    const yDol = H + OKNO_PODDASZA.parapet
    const szer = OKNO_PODDASZA.szerokosc
    const h = Math.min(OKNO_PODDASZA.wysokosc, Math.min(gora(x - szer / 2), gora(x + szer / 2)) - 0.15 - yDol)
    if (h >= 0.7) wynik.push({ x, yDol, szerokosc: szer, wysokosc: h, rodzaj: 'okno' })
  }
  return wynik
}

// Czy punkt [x, z] leży w obrysie skrzydła (z małym zapasem na styk ścian).
const wSkrzydle = (x, z, sk, zapas = 0.01) => {
  const c = Math.cos(sk.obrot * RAD)
  const s = Math.sin(sk.obrot * RAD)
  const dx = x - sk.srodek[0]
  const dz = z - sk.srodek[1]
  const a = dx * c - dz * s
  const b = dx * s + dz * c
  return Math.abs(a) <= sk.dlugosc / 2 + zapas && Math.abs(b) <= sk.szerokosc / 2 + zapas
}

/* ═══ Składanie bryły ═══════════════════════════════════════════════════════ */

const KLUCZ_KRAWEDZI = { okap: 'okap', kalenica: 'kalenica', naroze: 'naroza', szczytowa: 'szczytowe', zalamanie: 'zalamania' }

// Strona skrzydła, w którą patrzy ściana: przód = +z', prawo = +x'.
const stronaSciany = (n, obrot) => {
  const c = Math.cos(obrot * RAD)
  const s = Math.sin(obrot * RAD)
  const przod = n[0] * s + n[2] * c
  const prawo = n[0] * c - n[2] * s
  if (Math.abs(przod) >= Math.abs(prawo)) return przod > 0 ? 'przod' : 'tyl'
  return prawo > 0 ? 'prawo' : 'lewo'
}

const dodajPolacie = (wynik, sk, R, kd, y0) => {
  const d = sk.dach
  const pochyly = d.typ !== 'plaski'
  kd.plaszczyzny.forEach((pl, i) => {
    const w = pl.w.map((p) => R.punkt(p[0], p[1], p[2]))
    const nNewell = normalnaWielokata(w)
    const n = normalizuj(nNewell)
    const u = normalizuj(R.kierunek(pl.u[0], pl.u[1], pl.u[2]))
    const krawedzie = { okap: [], kalenica: [], naroza: [], szczytowe: [], zalamania: [] }
    pl.rodzaje.forEach((r, j) => krawedzie[KLUCZ_KRAWEDZI[r]].push([w[j], w[(j + 1) % w.length]]))
    const polac = {
      id: `${sk.id}-p${i}`,
      skrzydloId: sk.id,
      wierzcholki: w,
      normalna: n,
      nachylenie: Math.acos(Math.min(1, Math.max(-1, n[1]))) / RAD,
      azymut: azymutNormalnej(n),
      u,
      v: wektorowy(n, u),
      poczatek: w[0],
      krawedzie,
      rodzajeKrawedzi: pl.rodzaje,
      pole: dlugoscWektora(nNewell) / 2,
      grubosc: d.grubosc,
    }
    if (!pochyly) {
      // Prostokąt pod ukladNaDachuPlaskim: wnętrze attyki albo cały dach z okapem.
      const zmiana = d.attyka > 0 ? -2 * GRUBOSC_ATTYKI : 2 * d.okap
      polac.obszar = {
        srodek: [...sk.srodek], dlugosc: sk.dlugosc + zmiana, szerokosc: sk.szerokosc + zmiana,
        obrot: sk.obrot, y: y0 + sk.wysokoscScian,
      }
    }
    wynik.polacie.push(polac)
    if (!pochyly) return
    const poziomo = normalizuj([n[0], 0, n[2]])
    const przesuniecie = dodaj(skaluj(poziomo, 0.06), [0, -0.1, 0])
    krawedzie.okap.forEach(([a, b]) => {
      wynik.rynny.push({ skrzydloId: sk.id, od: dodaj(a, przesuniecie), do: dodaj(b, przesuniecie), promien: 0.075 })
    })
  })
  kd.kalenice.forEach(([a, b]) => wynik.kalenice.push([R.punkt(...a), R.punkt(...b)]))
  kd.naroza.forEach(([a, b]) => wynik.naroza.push([R.punkt(...a), R.punkt(...b)]))
  ;(kd.zalamania || []).forEach(([a, b]) => wynik.zalamania.push([R.punkt(...a), R.punkt(...b)]))
}

const DACHY_Z_PODDASZEM = ['dwuspadowy', 'mansardowy', 'naczolkowy']

const dodajSciany = (wynik, sk, R, kd, scK, B, poza, pomin = null) => {
  const d = sk.dach
  const H = sk.wysokoscScian
  const t = Math.tan(d.kat * RAD)
  // Okno w szczycie, gdy pod kalenicą jest co najmniej 2 m (dwuspadowy dopiero od 30°).
  const zPoddaszem = DACHY_Z_PODDASZEM.includes(d.typ) && (d.typ !== 'dwuspadowy' || d.kat >= 30 - 1e-9)
    && (d.typ === 'dwuspadowy' ? B * t : wysokoscDachu(kd.wysokosci, 0, 0) - H) >= 2 - 1e-9
  scK.forEach((sc) => {
    if (sc.klucz === pomin) return
    const profil = profilSciany(sc.prawy, sc.lewy, kd.wysokosci)
    const naSwiat = (s, h) => R.punkt(
      sc.prawy[0] + (sc.lewy[0] - sc.prawy[0]) * s, h, sc.prawy[1] + (sc.lewy[1] - sc.prawy[1]) * s,
    )
    const lewyG = R.punkt(sc.lewy[0], 0, sc.lewy[1])
    const prawyG = R.punkt(sc.prawy[0], 0, sc.prawy[1])
    const nW = R.kierunek(sc.n[0], sc.n[1], sc.n[2])
    const W = dlugoscWektora(odejmij(prawyG, lewyG))
    const strona = stronaSciany(nW, sk.obrot)
    wynik.sciany.push({
      skrzydloId: sk.id,
      wierzcholki: [lewyG, prawyG, ...profil.map(([s, h]) => naSwiat(s, h))],
      normalna: nW,
      szerokosc: W,
      wysokosc: Math.max(...profil.map((p) => p[1])),
      strona,
    })
    if (!sk.okna) return
    const wPrawo = normalizuj(odejmij(prawyG, lewyG))
    const poddasze = zPoddaszem && (sc.klucz === '+p' || sc.klucz === '-p')
    oknaSciany({ W, H, profil, strona, poddasze }).forEach((o) => {
      const srodek = dodaj(
        dodaj(lewyG, skaluj(wPrawo, o.x)),
        [nW[0] * PRZED_SCIANA, o.yDol + o.wysokosc / 2, nW[2] * PRZED_SCIANA],
      )
      // Ściana zasłonięta innym skrzydłem nie dostaje okien.
      if (!poza(dodaj(srodek, skaluj(nW, 0.15)))) return
      wynik.okna.push({
        skrzydloId: sk.id, srodek, normalna: nW, szerokosc: o.szerokosc, wysokosc: o.wysokosc, rodzaj: o.rodzaj,
      })
    })
  })
}

// Rury spustowe przy narożnikach ścian pod okapem: wylot pod rynną, kolano, pion do terenu.
const dodajRury = (wynik, sk, R, kd, scK, poza) => {
  const d = sk.dach
  const yOkapu = kd.yOkapu ?? sk.wysokoscScian - d.okap * Math.tan(d.kat * RAD)
  const odSciany = 0.12
  const doRynny = Math.max(odSciany, d.okap - 0.06)
  const widziane = new Set()
  kd.scianyOkapowe.forEach((klucz) => {
    const sc = scK.find((s) => s.klucz === klucz)
    ;[[sc.lewy, sc.prawy], [sc.prawy, sc.lewy]].forEach(([naroz, drugi]) => {
      const klNaroza = `${naroz[0].toFixed(4)},${naroz[1].toFixed(4)}`
      if (widziane.has(klNaroza)) return
      widziane.add(klNaroza)
      const dl = Math.hypot(drugi[0] - naroz[0], drugi[1] - naroz[1]) || 1
      const wzdluz = Math.min(0.25, dl / 2)
      const p = naroz[0] + (drugi[0] - naroz[0]) / dl * wzdluz
      const q = naroz[1] + (drugi[1] - naroz[1]) / dl * wzdluz
      const yWylotu = yOkapu - 0.12
      const yKolana = Math.max(0.3, yWylotu - Math.max(0.15, doRynny - odSciany))
      const dol = R.punkt(p + sc.n[0] * odSciany, 0.05, q + sc.n[2] * odSciany)
      if (!poza(dol)) return
      wynik.rury.push({
        skrzydloId: sk.id,
        wylot: R.punkt(p + sc.n[0] * doRynny, yWylotu, q + sc.n[2] * doRynny),
        gora: R.punkt(p + sc.n[0] * odSciany, yKolana, q + sc.n[2] * odSciany),
        dol,
      })
    })
  })
}

// Słupy wiaty: narożniki i co najwyżej co 3,5 m po obwodzie, do spodu płyty dachu.
const dodajSlupy = (wynik, sk, R, kd, A, B) => {
  const d = sk.dach
  const a = Math.max(0, A - SLUP.wciecie)
  const b = Math.max(0, B - SLUP.wciecie)
  const naroza = [[-a, -b], [-a, b], [a, b], [a, -b]]
  const grubPion = d.grubosc / Math.cos(d.kat * RAD)
  for (let i = 0; i < 4; i++) {
    const P = naroza[i]
    const Q = naroza[(i + 1) % 4]
    const n = Math.max(1, Math.ceil(Math.hypot(Q[0] - P[0], Q[1] - P[1]) / SLUP.rozstawMax - 1e-9))
    for (let k = 0; k < n; k++) {
      const p = P[0] + (Q[0] - P[0]) * k / n
      const q = P[1] + (Q[1] - P[1]) * k / n
      wynik.slupy.push({
        skrzydloId: sk.id,
        podstawa: R.punkt(p, 0, q),
        wysokosc: Math.max(0.1, wysokoscDachu(kd.wysokosci, p, q) - grubPion),
        przekroj: SLUP.przekroj,
      })
    }
  }
}

/* ═══ Skrzydła razem: dobudówki pod dachem domu, przycinanie i kosze ══════════ */

// Plan skrzydła: ramka kanoniczna i dach (przed przedłużeniem pod dach sąsiada).
const planSkrzydla = (sk, y0) => {
  const { obrot, A, B } = ramkaKanoniczna(sk)
  return { sk, y0, obrot, A, B, R: ramka(sk.srodek, obrot, y0), kd: dachKanoniczny(sk.dach, A, B, sk.wysokoscScian) }
}

// Punkt świata (x, z) w ramce kanonicznej planu: [p, q].
const doRamki = (pl, x, z) => {
  const c = Math.cos(pl.obrot * RAD)
  const s = Math.sin(pl.obrot * RAD)
  const dx = x - pl.sk.srodek[0]
  const dz = z - pl.sk.srodek[1]
  return [dx * c - dz * s, dx * s + dz * c]
}

// Wysokość dachu skrzydła nad punktem świata (x, z) w zasięgu okapów albo null.
const wysokoscPlanu = (pl, x, z) => {
  const [p, q] = doRamki(pl, x, z)
  const zs = pl.kd.zasieg
  if (p < zs.p0 - 1e-9 || p > zs.p1 + 1e-9 || q < zs.q0 - 1e-9 || q > zs.q1 + 1e-9) return null
  return pl.y0 + wysokoscDachu(pl.kd.wysokosci, p, q)
}

const KIERUNKI_SCIAN = { przod: [0, 1], tyl: [0, -1], prawo: [1, 0], lewo: [-1, 0] }

/**
 * Skrzydło przy ścianie innego (`przylega`): zapamiętuje stronę ramki kanonicznej zwróconą do tamtego skrzydła.
 * Gdy przy ścianie jest koniec dachu (szczyt albo naczółek), dach wchodzi pod dach sąsiada aż do miejsca,
 * w którym jego kalenica chowa się pod tamtym dachem: zamiast szczytu przy ścianie są kosze. Skrzydło
 * wyższe od sąsiada zostaje bez zmian.
 */
const dopasujDoRodzica = (pl, plany) => {
  const { sk } = pl
  const rodzic = sk.przylega && plany.find((x) => x.sk.id === sk.przylega.do && x !== pl)
  if (!rodzic) return
  const t = rodzic.sk.obrot * RAD
  const k = KIERUNKI_SCIAN[sk.przylega.sciana] || KIERUNKI_SCIAN.przod
  // Normalna ściany rodzica w świecie [x, z]; skrzydło patrzy na rodzica w przeciwną stronę.
  const na = [k[0] * Math.cos(t) + k[1] * Math.sin(t), -k[0] * Math.sin(t) + k[1] * Math.cos(t)]
  let strona = null
  let najlepiej = -Infinity
  for (const [klucz, v] of [['+p', [1, 0, 0]], ['-p', [-1, 0, 0]], ['+q', [0, 0, 1]], ['-q', [0, 0, -1]]]) {
    const kier = pl.R.kierunek(...v)
    const il = -(kier[0] * na[0] + kier[2] * na[1])
    if (il > najlepiej) { najlepiej = il; strona = klucz }
  }
  pl.rodzic = rodzic
  pl.stronaRodzica = strona
  const d = sk.dach
  if (!DACHY_PRZEDLUZANE.includes(d.typ) || strona[1] !== 'p') return
  // Najwyższy punkt przekroju dachu przy ścianie rodzica (bez połaci końcowej od tej strony).
  const bez = modelDachu(d, pl.A, pl.B, sk.wysokoscScian).plaszczyzny.filter((x) => x.strona !== strona).map((x) => x.c)
  const pKonca = strona === '+p' ? pl.A : -pl.A
  let qK = 0
  let yK = -Infinity
  for (let q = -pl.B; q <= pl.B + 1e-9; q += 0.05) {
    const y = wysokoscDachu(bez, pKonca, q)
    if (y > yK) { yK = y; qK = q }
  }
  const znak = strona === '+p' ? 1 : -1
  const zasieg = Math.hypot(rodzic.sk.dlugosc, rodzic.sk.szerokosc)
  for (let s = 0; s <= zasieg; s += 0.05) {
    const P = pl.R.punkt(pKonca + znak * s, 0, qK)
    const yR = wysokoscPlanu(rodzic, P[0], P[2])
    if (yR != null && yR >= pl.y0 + yK + 0.02) {
      pl.kd = dachKanoniczny(d, pl.A, pl.B, sk.wysokoscScian, { strona, dlugosc: s + 0.2 })
      pl.przedluzony = true
      return
    }
  }
}

/**
 * Ściana skrzydła przy ścianie rodzica leży w jej płaszczyźnie (migotanie w widoku): pomijamy ją, gdy dach
 * wszedł pod dach rodzica albo gdy cała mieści się w ścianie i pod dachem rodzica. Inaczej (np. dobudówka
 * wyższa albo szersza niż ściana domu) zostaje.
 */
const scianaDoPominiecia = (pl) => {
  if (!pl.rodzic || pl.rodzic.sk.rodzaj === 'wiata') return null
  if (pl.przedluzony) return pl.stronaRodzica
  const sc = scianyKanoniczne(pl.A, pl.B).find((x) => x.klucz === pl.stronaRodzica)
  const attyka = pl.sk.dach.typ === 'plaski' ? pl.sk.dach.attyka || 0 : 0
  const profil = profilSciany(sc.prawy, sc.lewy, pl.kd.wysokosci)
  const W = Math.hypot(sc.lewy[0] - sc.prawy[0], sc.lewy[1] - sc.prawy[1])
  for (let k = 0; k <= 20; k++) {
    const s = k / 20
    const P = pl.R.punkt(sc.prawy[0] + (sc.lewy[0] - sc.prawy[0]) * s, 0, sc.prawy[1] + (sc.lewy[1] - sc.prawy[1]) * s)
    if (!wSkrzydle(P[0], P[2], pl.rodzic.sk, 0.05)) return null
    const yR = wysokoscPlanu(pl.rodzic, P[0], P[2])
    if (yR == null || yR < pl.y0 + wysokoscProfilu(profil, (1 - s) * W, W) + attyka - 0.01) return null
  }
  return pl.stronaRodzica
}

// Wysokość płaszczyzny połaci nad (x, z).
const yPolaci = (p, x, z) => {
  const [nx, ny, nz] = p.normalna
  const a = p.wierzcholki[0]
  return a[1] - (nx * (x - a[0]) + nz * (z - a[2])) / ny
}

const rzutPolaci = (p) => p.wierzcholki.map((v) => [v[0], v[2]])

const wewnatrz2 = ([x, z], w) => {
  let wyn = false
  for (let i = 0, j = w.length - 1; i < w.length; j = i, i += 1) {
    if ((w[i][1] > z) !== (w[j][1] > z) && x < ((w[j][0] - w[i][0]) * (z - w[i][1])) / (w[j][1] - w[i][1]) + w[i][0]) wyn = !wyn
  }
  return wyn
}

// Przedział [t0, t1] odcinka ab (rzut [x, z]) leżący w wielokącie wypukłym albo null (Cyrus–Beck).
const odcinekWWielokacie = (a, b, w) => {
  const ww = pole2(w) < 0 ? [...w].reverse() : w
  const d = [b[0] - a[0], b[1] - a[1]]
  let t0 = 0
  let t1 = 1
  for (let i = 0; i < ww.length; i++) {
    const p = ww[i]
    const q = ww[(i + 1) % ww.length]
    const n = [p[1] - q[1], q[0] - p[0]]
    const licznik = n[0] * (a[0] - p[0]) + n[1] * (a[1] - p[1])
    const mian = n[0] * d[0] + n[1] * d[1]
    if (Math.abs(mian) < 1e-12) {
      if (licznik < 0) return null
    } else if (mian > 0) t0 = Math.max(t0, -licznik / mian)
    else t1 = Math.min(t1, -licznik / mian)
    if (t0 > t1) return null
  }
  return t1 - t0 > 1e-6 ? [t0, t1] : null
}

/**
 * Dachy skrzydeł razem. Z każdej połaci znika część schowana we wnętrzu innego skrzydła pod jego dachem
 * (`czesci`: widoczne wypukłe kawałki, `pole`: ich pole); część pod samym okapem sąsiada zostaje, bo to
 * prawdziwy dach (moduły omija ją `pod_dachem` w przeszkody.js). Tam, gdzie połacie dwóch skrzydeł się
 * przecinają i obie są na wierzchu, jest kosz (`kosze`). Rynny schowane w innym skrzydle są przycinane.
 */
const przytnijDachy = (wynik) => {
  const P = wynik.polacie
  const obrysy = new Map(wynik.obrysy.map((o) => [o.skrzydloId, o.punkty]))
  const pochyla = (p) => Math.abs(p.normalna[1]) > 1e-6
  for (const p of P) {
    if (!pochyla(p)) continue
    const R2 = rzutPolaci(p)
    let czesci = [R2]
    let zmiana = false
    for (const q of P) {
      const obrys = obrysy.get(q.skrzydloId)
      if (q.skrzydloId === p.skrzydloId || !obrys || !pochyla(q)) continue
      let w = przytnijWypuklym(R2, rzutPolaci(q))
      if (w.length) w = przytnijWypuklym(w, obrys)
      if (!w.length) continue
      // q nad p: y_q − y_p > 1 cm, różnica płaszczyzn liniowa w (x, z).
      const f = (x, z) => yPolaci(q, x, z) - yPolaci(p, x, z) - 0.01
      const f0 = f(0, 0)
      w = przytnijPolplaszczyzna(w, f(1, 0) - f0, f(0, 1) - f0, -f0)
      if (w.length < 3 || Math.abs(pole2(w)) < 1e-3) continue
      czesci = czesci.flatMap((c) => odejmijWypukly(c, w))
      zmiana = true
    }
    if (!zmiana) continue
    czesci = czesci.filter((c) => Math.abs(pole2(c)) > 1e-3)
    p.czesci = czesci.map((c) => c.map(([x, z]) => [x, yPolaci(p, x, z), z]))
    p.pole = czesci.reduce((s, c) => s + Math.abs(pole2(c)), 0) / Math.abs(p.normalna[1])
  }

  wynik.kosze = []
  for (let i = 0; i < P.length; i++) {
    for (let j = i + 1; j < P.length; j++) {
      const a = P[i]
      const b = P[j]
      if (a.skrzydloId === b.skrzydloId || !pochyla(a) || !pochyla(b)) continue
      const C = przytnijWypuklym(rzutPolaci(a), rzutPolaci(b))
      if (!C.length) continue
      const f = (x, z) => yPolaci(a, x, z) - yPolaci(b, x, z)
      const pkt = []
      for (let k = 0; k < C.length; k++) {
        const u = C[k]
        const v = C[(k + 1) % C.length]
        const fu = f(u[0], u[1])
        const fv = f(v[0], v[1])
        if ((fu > 0) !== (fv > 0) && Math.abs(fu - fv) > 1e-12) {
          const t = fu / (fu - fv)
          pkt.push([u[0] + (v[0] - u[0]) * t, u[1] + (v[1] - u[1]) * t])
        }
      }
      if (pkt.length < 2) continue
      const m = pkt[0]
      const n = pkt[pkt.length - 1]
      if (Math.hypot(n[0] - m[0], n[1] - m[1]) < 0.2) continue
      const sr = [(m[0] + n[0]) / 2, (m[1] + n[1]) / 2]
      const ySr = yPolaci(a, sr[0], sr[1])
      // Kosz tylko na wierzchu: żadna inna połać nie przykrywa go od góry.
      if (P.some((c) => c !== a && c !== b && pochyla(c) && wewnatrz2(sr, rzutPolaci(c)) && yPolaci(c, sr[0], sr[1]) > ySr + 0.02)) continue
      wynik.kosze.push([[m[0], yPolaci(a, m[0], m[1]), m[1]], [n[0], yPolaci(a, n[0], n[1]), n[1]]])
    }
  }

  wynik.rynny = wynik.rynny.flatMap((r) => {
    let kawalki = [[0, 1]]
    for (const [id, obrys] of obrysy) {
      if (id === r.skrzydloId) continue
      const t = odcinekWWielokacie([r.od[0], r.od[2]], [r.do[0], r.do[2]], obrys)
      if (!t) continue
      kawalki = kawalki.flatMap(([a, b]) => [[a, Math.min(b, t[0])], [Math.max(a, t[1]), b]].filter(([x, y]) => y - x > 1e-6))
    }
    const dl = dlugoscWektora(odejmij(r.do, r.od))
    const pkt = (t) => dodaj(r.od, skaluj(odejmij(r.do, r.od), t))
    return kawalki.filter(([a, b]) => (b - a) * dl > 0.1).map(([a, b]) => ({ ...r, od: pkt(a), do: pkt(b) }))
  })
}

const KRAWEDZ_OBRYSU = { '-p': 0, '+q': 1, '+p': 2, '-q': 3 }

const dodajSkrzydlo = (wynik, pl, inne) => {
  const { sk, A, B, R, kd, y0 } = pl
  const d = sk.dach
  const scK = scianyKanoniczne(A, B)
  const poza = (p) => !inne.some((o) => wSkrzydle(p[0], p[2], o))
  const pomin = scianaDoPominiecia(pl)

  dodajPolacie(wynik, sk, R, kd, y0)
  const obrys = [[-A, -B], [-A, B], [A, B], [A, -B]].map(([p, q]) => {
    const P = R.punkt(p, 0, q)
    return [P[0], P[2]]
  })
  wynik.obrysy.push({ skrzydloId: sk.id, punkty: obrys })
  if (d.typ === 'plaski' && d.attyka > 0) {
    wynik.attyki.push({
      skrzydloId: sk.id, obrys: obrys.map((p) => [...p]),
      y0: y0 + sk.wysokoscScian, y1: y0 + sk.wysokoscScian + d.attyka, grubosc: GRUBOSC_ATTYKI,
      // Bok attyki przy ścianie domu (indeks krawędzi obrysu) pomijamy razem ze ścianą.
      ...(pomin ? { pominKrawedz: KRAWEDZ_OBRYSU[pomin] } : {}),
    })
  }
  if (sk.rodzaj === 'wiata') dodajSlupy(wynik, sk, R, kd, A, B)
  else dodajSciany(wynik, sk, R, kd, scK, B, poza, pomin)
  if (d.typ !== 'plaski') dodajRury(wynik, sk, R, kd, scK, poza)
}

/**
 * Bryła z listy skrzydeł (opis pól w nagłówku zadania i w PLAN_PROJEKT_DACHU_3D.md).
 * Skrzydło przy ścianie innego (`przylega`) wchodzi dachem pod jego dach i traci ścianę przy tej ścianie;
 * dachy wszystkich skrzydeł są potem przycinane do części widocznych, a ich przecięcia dają kosze
 * (`przytnijDachy`). Okna i rury zasłonięte innym skrzydłem są pomijane.
 */
export const zbudujBryle = (budynek = {}) => {
  const y0 = budynek.poziomTerenu || 0
  const skrzydla = (budynek.skrzydla || []).map(normalizujSkrzydlo)
  const wynik = {
    sciany: [], polacie: [], kalenice: [], naroza: [], zalamania: [], kosze: [], rynny: [], rury: [], okna: [],
    attyki: [], slupy: [], obrysy: [], wysokoscMax: y0,
  }
  const plany = skrzydla.map((sk) => planSkrzydla(sk, y0))
  plany.forEach((pl) => dopasujDoRodzica(pl, plany))
  plany.forEach((pl) => dodajSkrzydlo(wynik, pl, skrzydla.filter((o) => o !== pl.sk)))
  przytnijDachy(wynik)
  let max = y0
  wynik.polacie.forEach((p) => p.wierzcholki.forEach((v) => { max = Math.max(max, v[1]) }))
  wynik.sciany.forEach((s) => s.wierzcholki.forEach((v) => { max = Math.max(max, v[1]) }))
  wynik.attyki.forEach((a) => { max = Math.max(max, a.y1) })
  wynik.wysokoscMax = max
  return wynik
}
